import {
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import { AccessibleProduct, PAGES } from "@dune/types";
import {
	useGetGFAFullDetails,
	useGetGFABinderAmendment,
} from "../../../../api/queries/gfa.queries";
import { useGetBrokerProducts } from "../../../../api/queries/useGetBrokerProducts";
import { PriceInput } from "../../../../components/inputs/PriceInput";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useCreateGFAOffer } from "../../../../api/mutations/gfa.mutations";
import { parseCommissionRate, roundTo2Digits } from "@dune/utils";
import { useNavigate } from "react-router-dom";

interface AnalyzeGFASubmissionModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	gfaId: number;
}

interface AnalyzeGFAFormData {
	applicableRate: number;
	duneFees: number;
	ancillaryBrokerageFees: number;
	validateGFA: boolean;
}

const AnalyzeGFASubmissionModal: React.FC<AnalyzeGFASubmissionModalProps> = ({
	open,
	onOpenChange,
	gfaId,
}) => {
	const navigate = useNavigate();
	const { data: gfaData } = useGetGFAFullDetails(gfaId.toString());
	const { mutate: createOffer, isPending: isSubmitting } = useCreateGFAOffer();

	const brokerSocietyId = gfaData?.brokerSociety?.id;
	const { data: brokerProducts } = useGetBrokerProducts(brokerSocietyId ?? 0);
	const { data: gfaBinderAmendment } = useGetGFABinderAmendment(open);

	const commissionRates = useMemo(() => {
		const brokerGFAProduct = brokerProducts?.find(
			(product: AccessibleProduct) => product.gfaProduct === true,
		);

		const duneRate = gfaBinderAmendment?.commissionRate
			? gfaBinderAmendment.commissionRate / 100
			: 0;

		return {
			apporteur: parseCommissionRate(brokerGFAProduct?.commission),
			dune: duneRate,
		};
	}, [brokerProducts, gfaBinderAmendment]);
	const salePrice = gfaData?.gfaFinancings?.estimatedSalePriceTTC ?? 0;

	const form = useForm<AnalyzeGFAFormData>({
		defaultValues: {
			applicableRate: 0,
			duneFees: 0,
			ancillaryBrokerageFees: 0,
			validateGFA: false,
		},
	});

	const { control, watch } = form;
	const applicableRate = watch("applicableRate");
	const duneFees = watch("duneFees");
	const ancillaryBrokerageFees = watch("ancillaryBrokerageFees");

	const calculations = useMemo(() => {
		const primeTTC = roundTo2Digits(salePrice * (applicableRate / 100));
		const primeClientTTC = roundTo2Digits(
			primeTTC + ancillaryBrokerageFees + duneFees,
		);
		const commissionApporteur = roundTo2Digits(
			primeTTC * commissionRates.apporteur,
		);
		const remunerationApporteur = roundTo2Digits(
			commissionApporteur + ancillaryBrokerageFees,
		);
		const commissionDUNE = roundTo2Digits(primeTTC * commissionRates.dune);
		const remunerationDUNE = roundTo2Digits(commissionDUNE + duneFees);

		return {
			primeTTC,
			primeClientTTC,
			commissionApporteur,
			remunerationApporteur,
			commissionDUNE,
			remunerationDUNE,
		};
	}, [
		salePrice,
		applicableRate,
		duneFees,
		ancillaryBrokerageFees,
		commissionRates,
	]);

	const handleValidate = async () => {
		const formValues = form.getValues();
		if (formValues.validateGFA !== true) {
			toast.error(
				"Veuillez confirmer la validation du chantier GFA avant de continuer",
			);
			return;
		}
		createOffer(
			{
				gfaId,
				applicableRate: formValues.applicableRate,
				duneFees: formValues.duneFees,
				ancillaryBrokerageFees: formValues.ancillaryBrokerageFees,
				primeTTC: calculations.primeTTC,
				primeClientTTC: calculations.primeClientTTC,
				commissionApporteur: calculations.commissionApporteur,
				remunerationApporteur: calculations.remunerationApporteur,
				commissionDune: calculations.commissionDUNE,
				remunerationDune: calculations.remunerationDUNE,
			},
			{
				onSuccess: () => {
					onOpenChange(false);
					navigate(PAGES.GFA.show.withParams(gfaId.toString()));
				},
			},
		);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Validation</DialogTitle>
				</DialogHeader>

				<div className="space-y-6">
					<div className="space-y-4">
						<h3 className="text-lg font-semibold text-gray-900">
							Tarification
						</h3>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Taux applicable
								</div>
							</div>
							<div className="col-span-1">
								<Controller
									name="applicableRate"
									control={control}
									render={({ field }) => (
										<PriceInput
											value={field.value}
											onChange={(value) => {
												field.onChange(Number(value) || 0);
											}}
											iconOrText="%"
											className="bg-yellow-50 border-yellow-200"
											decimals={4}
										/>
									)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Assiette de prime
								</div>
							</div>
							<div className="col-span-1">
								<PriceInput
									value={salePrice}
									onChange={() => {}}
									iconOrText="€"
									disabled={true}
									className="bg-gray-100"
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Prime TTC
								</div>
							</div>
							<div className="col-span-1">
								<PriceInput
									value={calculations.primeTTC}
									onChange={() => {}}
									iconOrText="€"
									disabled={true}
									className="bg-gray-200"
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Frais DUNE
								</div>
							</div>
							<div className="col-span-1">
								<Controller
									name="duneFees"
									control={control}
									render={({ field }) => (
										<PriceInput
											value={field.value}
											onChange={(value) => {
												field.onChange(Number(value) || 0);
											}}
											iconOrText="€"
											className="bg-yellow-50 border-yellow-200"
										/>
									)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Frais Annexes de courtage
								</div>
							</div>
							<div className="col-span-1">
								<Controller
									name="ancillaryBrokerageFees"
									control={control}
									render={({ field }) => (
										<PriceInput
											value={field.value}
											onChange={(value) => {
												field.onChange(Number(value) || 0);
											}}
											iconOrText="€"
											className="bg-yellow-50 border-yellow-200"
										/>
									)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Prime Client TTC
								</div>
							</div>
							<div className="col-span-1">
								<PriceInput
									value={calculations.primeClientTTC}
									onChange={() => {}}
									iconOrText="€"
									disabled={true}
									className="bg-gray-200"
								/>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h3 className="text-lg font-semibold text-gray-900">
							Commission Apporteur
						</h3>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Commission Apporteur
								</div>
							</div>
							<div className="col-span-1">
								<PriceInput
									value={calculations.commissionApporteur}
									onChange={() => {}}
									iconOrText="€"
									disabled={true}
									className="bg-gray-200"
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Rémunération Apporteur
								</div>
							</div>
							<div className="col-span-1">
								<PriceInput
									value={calculations.remunerationApporteur}
									onChange={() => {}}
									iconOrText="€"
									disabled={true}
									className="bg-gray-200"
								/>
							</div>
						</div>
					</div>

					<div className="space-y-4">
						<h3 className="text-lg font-semibold text-gray-900">
							Commission DUNE
						</h3>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Commission DUNE
								</div>
							</div>
							<div className="col-span-1">
								<PriceInput
									value={calculations.commissionDUNE}
									onChange={() => {}}
									iconOrText="€"
									disabled={true}
									className="bg-gray-200"
								/>
							</div>
						</div>

						<div className="grid grid-cols-3 gap-4 items-center">
							<div className="col-span-1">
								<div className="text-sm font-medium text-gray-700">
									Rémunération DUNE
								</div>
							</div>
							<div className="col-span-1">
								<PriceInput
									value={calculations.remunerationDUNE}
									onChange={() => {}}
									iconOrText="€"
									disabled={true}
									className="bg-gray-200"
								/>
							</div>
						</div>
					</div>

					<div className="space-y-4 border-t pt-4">
						<div className="flex items-center gap-4">
							<div className="text-sm font-medium text-gray-700">
								Souhaitez-vous valider le chantier GFA?
							</div>
						</div>
						<div className="flex items-center gap-6">
							<Controller
								name="validateGFA"
								control={control}
								render={({ field }) => (
									<div className="flex items-center gap-2">
										<Checkbox
											id="validate-yes"
											checked={field.value === true}
											onCheckedChange={(checked) => {
												field.onChange(checked === true ? true : undefined);
											}}
										/>
										<label
											htmlFor="validate-yes"
											className="text-sm text-gray-700 cursor-pointer"
										>
											Oui
										</label>
									</div>
								)}
							/>
							<Controller
								name="validateGFA"
								control={control}
								render={({ field }) => (
									<div className="flex items-center gap-2">
										<Checkbox
											id="validate-no"
											checked={field.value === false}
											onCheckedChange={(checked) => {
												field.onChange(checked === true ? false : undefined);
											}}
										/>
										<label
											htmlFor="validate-no"
											className="text-sm text-gray-700 cursor-pointer"
										>
											Non
										</label>
									</div>
								)}
							/>
						</div>
					</div>

					<div className="flex justify-end gap-3 pt-4 border-t">
						<Button
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={isSubmitting}
						>
							Annuler
						</Button>
						<Button
							variant="default"
							onClick={handleValidate}
							disabled={isSubmitting}
							className="bg-black text-white hover:bg-gray-800"
						>
							{isSubmitting ? "Validation..." : "Valider"}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default AnalyzeGFASubmissionModal;
